/**
 * The catalogue's feature keys — mirrors `FEATURE_KEY` in the API's
 * `config/features.ts`. A feature not active for the school on the Feature
 * tracker is hidden from every menu and screen; the server refuses it either way.
 */
export const FEATURE_KEYS = [
  'CLASSES',
  'SUBJECTS',
  'ATTENDANCE',
  'TIMETABLE',
  'HOMEWORK',
  'ACTIVITY',
  'EXAM',
  'RECORDING',
  'STUDENT_RECORDS',
  'HOUSES',
  'STAFF',
  'STAFF_ATTENDANCE',
  'CLASS_NOTICE',
  'INSTITUTE_NOTICE',
  'MESSAGING',
  'FEES',
  'FEE_REMINDERS',
  'PAYROLL',
  'CALENDAR',
  'ROLES',
  'REPORTS',
  'TRANSPORT',
  'DASHBOARD',
  'INSTITUTE_PROFILE',
  'LEAVE_REQUESTS',
  'SUPPORT',
  'CHANGE_PASSWORD',
  'MY_PROFILE',
  'SETTINGS',
  'STUDENT_PANEL',
  'TEACHER_PANEL',
  'PARENT_PANEL',
] as const;

export type FeatureKey = (typeof FEATURE_KEYS)[number];

/**
 * Each feature's name when the catalogue has none to give — before sign-in, or
 * for a key the catalogue has since lost. The catalogue's name wins whenever
 * there is one (`AuthUser.featureNames`), so a rename in the platform console
 * renames the sidebar and the Roles screen together. Matches the names
 * `20261006240000_rename_features_to_sidebar_names` set.
 */
export const FEATURE_LABEL: Record<FeatureKey, string> = {
  CLASSES: 'Classes',
  SUBJECTS: 'Subjects',
  ATTENDANCE: 'Attendance',
  TIMETABLE: 'Timetable',
  HOMEWORK: 'Homework',
  ACTIVITY: 'Class Activities',
  EXAM: 'Exams',
  RECORDING: 'Recorded Classes',
  STUDENT_RECORDS: 'Students',
  HOUSES: 'Houses',
  STAFF: 'Staff',
  STAFF_ATTENDANCE: 'Staff Attendance',
  CLASS_NOTICE: 'Class Notices',
  INSTITUTE_NOTICE: 'Notice Board',
  MESSAGING: 'Notifications',
  FEES: 'Fees',
  FEE_REMINDERS: 'Fee Reminders',
  PAYROLL: 'Payroll',
  CALENDAR: 'Academic Calendar',
  ROLES: 'Roles',
  REPORTS: 'Daily Report',
  TRANSPORT: 'Transport',
  DASHBOARD: 'Dashboard',
  INSTITUTE_PROFILE: 'Institute Profile',
  LEAVE_REQUESTS: 'Leave Requests',
  SUPPORT: 'Support',
  CHANGE_PASSWORD: 'Change password',
  MY_PROFILE: 'My Profile',
  SETTINGS: 'Settings',
  STUDENT_PANEL: 'Student panel',
  TEACHER_PANEL: 'Teacher panel',
  PARENT_PANEL: 'Parent panel',
};

/** A feature's one name: the catalogue's when known, the default otherwise. */
export function featureName(
  feature: FeatureKey,
  names: Partial<Record<FeatureKey, string>> | undefined,
): string {
  return names?.[feature] || FEATURE_LABEL[feature];
}
